'use client';

import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { useRef } from 'react';

import { getTradingCompetitionsListApi } from '@flipster/apis/v1/tradingCompetition';
import { CompetitionStatusEnum, CompetitionStatusType } from '@flipster/apis/v1/tradingCompetition/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system';
import { Carousel } from '@flipster/react/design-system-legacy/carousel';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { breakpointMB } from '@flipster/utils/constants/breakpoint';
import { DateFormatter, isSameUTCYear } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { maxWStyles, sectionSmallHeadingStyles } from '@web/app/[locale]/(web)/_main-page/_styles';
import privateTrophy from '@web/app/[locale]/(web)/_main-page/assets/private-trophy.webp';
import publicTrophy from '@web/app/[locale]/(web)/_main-page/assets/public-trophy.webp';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';

const CurrentActivities = () => {
  const { isCookieLoggedIn } = useAuth();

  const { data } = useQuery({
    queryKey: ['getTradingCompetitionsListApi', isCookieLoggedIn],
    queryFn: () => getTradingCompetitionsListApi(),
    select: (res) => res.data,
  });

  const intersectionRef = useRef<HTMLHeadingElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Current activities');

  const isMounted = useMounted();

  if (!isMounted || !data?.competitions.length) return null;

  const initIndex = data.competitions.findIndex((c) => checkActive(c.competitionStatus));

  return (
    <Carousel
      initialSlide={initIndex}
      slidesPerView='auto'
      withOverflow
      spaceBetween={16}
      breakpoints={{ [breakpointMB]: { spaceBetween: 32 } }}
      css={{ ...maxWStyles, pt: { base: 48, tablet: 64 }, pb: { base: 48, tablet: 96 } }}
      title={
        <h2 ref={intersectionRef} className={css(sectionSmallHeadingStyles)}>
          Current activities
        </h2>
      }
      navigator={<Carousel.Navigator css={{ tabletDown: { display: 'none' } }} />}
    >
      {data.competitions.map((competition, index) => {
        const isSameYear = isSameUTCYear(competition.competitionStartTime, competition.competitionEndTime);
        const isActive = checkActive(competition.competitionStatus);

        return (
          <Carousel.Slide css={css.raw(itemStyles, isActive && activeItemStyles)} key={competition.title + index}>
            <p className={css(dateStyles)}>
              <Datetime value={competition.competitionStartTime} format={DateFormatter.ShortYearDate} isUTC />
              <span> - </span>
              <Datetime
                value={competition.competitionEndTime}
                format={isSameYear ? 'MM.dd' : DateFormatter.ShortYearDate}
                isUTC
                withUTCText
              />
            </p>
            <div
              className={flex({
                w: '100%',
                flex: 1,
                tabletDown: { flexDirection: 'column-reverse', justifyContent: 'space-between' },
              })}
            >
              <div
                className={stack({ flex: 1, h: '100%', justifyContent: { base: 'flex-end', tablet: 'space-between' } })}
              >
                <div className={css({ mb: 24 })}>
                  {competition.hostName && (
                    <div
                      className={hstack({
                        textStyle: 'Text/L/Strong',
                        color: 'content.accent.promotion',
                        gap: 4,
                        mb: 8,
                      })}
                    >
                      <LockClosedIcon size={16} />
                      <p>Hosted by {competition.hostName}</p>
                    </div>
                  )}
                  <p
                    className={css({
                      display: '-webkit-box',
                      textStyle: 'Title/M',
                      maxH: 78,
                      mb: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      lineClamp: { base: 3, tablet: 2 },
                      boxOrient: 'vertical',
                    })}
                  >
                    {competition.title}
                  </p>
                  <p className={css({ textStyle: 'Title/XS' })}>
                    <span>Total prize pool </span>
                    <Formatted.Number value={competition.prizePool} precision={2} prefix='$' toFixed />
                  </p>
                </div>
                <Button
                  hierarchy='primary'
                  color='accent'
                  size='medium'
                  as='a'
                  label='Learn more'
                  href={`/competitions/${competition.competitionUrl}`}
                  onClick={() => {
                    segment.track(MainEvent.MainPageActivityClick, {
                      list_order: index,
                      activity_name: competition.title,
                      activity_start_date: competition.competitionStartTime,
                      activity_end_date: competition.competitionStartTime,
                    });
                  }}
                  css={{ tablet: { maxW: 200 } }}
                />
              </div>
              <div className={flex({ justify: 'flex-end' })}>
                <Image
                  src={competition.competitionType === 'HOSTED_BY_INFLUENCER' ? privateTrophy : publicTrophy}
                  alt='competition trophy'
                  width={200}
                  height={200}
                  className={css({ w: { base: 80, tablet: 200 }, h: 'auto' })}
                />
              </div>
            </div>
          </Carousel.Slide>
        );
      })}
    </Carousel>
  );
};

export default CurrentActivities;

function checkActive(competitionStatus: CompetitionStatusType) {
  return competitionStatus === CompetitionStatusEnum.Ongoing || competitionStatus === CompetitionStatusEnum.Upcoming;
}

const itemStyles = stack.raw({
  pos: 'relative',
  align: 'flex-start',
  h: 380,
  w: { base: 'calc(100% - 80px)', tablet: '100%' },
  border: '1px solid',
  borderColor: 'border.default.level-2',
  rounded: { base: 8, tablet: 16 },
  p: { base: '16px 16px 32px', tablet: 56 },
  bgColor: 'background.default.level-1',
});

const activeItemStyles = css.raw({
  '--active-color': 'colors.border.accent.info',
  bgColor: 'background.default.level-0',
  borderColor: 'var(--active-color)',
  boxShadow: '0 0 16px 0 var(--active-color)',
});

const dateStyles = css.raw({
  textStyle: 'Text/L/Regular',
  px: { base: 10, tablet: 16 },
  py: { base: 2, tablet: 6 },
  mb: 32,
  rounded: { base: 12, tablet: 20 },
  bgColor: 'background.default.level-3',
});
